import Controller from '@ember/controller';
import { action, set, get } from '@ember/object';
import { service } from '@ember/service';
import { isPresent, isEmpty } from '@ember/utils';
import { tracked } from '@glimmer/tracking';
import { task, all } from 'ember-concurrency';
import { alias, filter, filterBy } from 'macro-decorators';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import urlBuilder from 'garaje/utils/url-builder';
import _isEqual from 'lodash/isEqual';
import { createDeskStorePayload } from 'garaje/utils/bulk-map-operations/bulk-map-operations-helpers';
import {
  buildCustomEmployeeItem,
  getFlattenedEmployees,
  addFeatureToOverview,
  updateFeatureInOverview,
  deleteFeatureInOverview,
  expandSectionOnFeatureSelect,
} from 'garaje/utils/resource-overview';
import { POLYGON_DRAWING_OPTIONS } from 'garaje/utils/polygon';
import leaflet from 'leaflet';
import { cached } from 'tracked-toolbox';

export default class MapsEditShowController extends Controller {
  @service flashMessages;
  @service featureFlags;
  @service featureConfig;
  @service store;
  @service state;
  @service router;
  @service ajax;
  @service metrics;
  @service abilities;
  @service resourceOverview;

  @alias('state.currentLocation') currentLocation;
  // TODO: need hasMaxActiveDesks

  @filter('desks', (desk) => desk.hasDirtyAttributes || desk.hasDirtyRelationships) dirtyDesks;
  @filterBy('mapFeatures', 'hasDirtyAttributes') dirtyFeatures;
  @filter('mapFeatures', (feature) => feature.hasError || !feature.name) errorFeatures;
  @filterBy('desks', 'isDeleted', false) useableDesks = [];

  @tracked employees = [];
  @tracked rooms = [];
  @tracked deliveryAreas = [];
  @tracked desks = [];
  @tracked amenities = [];
  @tracked neighborhoods = [];
  @tracked resourceInsights = [];

  @tracked employeesPage = 0;
  @tracked employeesCount = 0;
  @tracked limit = 25;
  @tracked selectedFeature;
  @tracked mapFeatures = [];
  @tracked deletableFeatures = [];
  @tracked paymentSources = [];
  @tracked placingResource;
  @tracked shouldResetMap = false;

  @tracked leafletMap = null;
  @tracked drawingArea = null;

  @tracked gqlMapFeatures = {
    desks: {},
    rooms: {},
    'points-of-interest': {},
  };
  @tracked gqlEmployees = {
    unassigned: [],
    assigned: [],
  };
  @tracked selectedPanelView = '';
  @tracked selectedNodeId = null;
  @tracked selectedEmployeeEmail = null;
  @tracked shouldShowEmployeeForm = false;
  @tracked expandedMap = [
    'resources/desks',
    'resources/rooms',
    'resources/points-of-interest',
    'seating/assigned',
    'seating/unassigned',
  ];
  @tracked deskResourceSelections = 0;

  get hasShownAutoGenerationTooltip() {
    return this.deskResourceSelections > 1;
  }

  @action
  setExpandedMap(path) {
    const selectedView = path.split('/')[0];
    if (this.expandedMap.includes(path)) {
      // Remove the path from the expandedMap. If item is "foo/bar/bif" a path of "foo" will remove the entire item.
      this.expandedMap = this.expandedMap.filter((item) => !item.includes(path));
      this.metrics.trackEvent('Collapsed section in edit map', { panel: selectedView, section: path });
    } else {
      this.expandedMap = [...this.expandedMap, path];
      this.metrics.trackEvent('Expanded section in edit map', { panel: selectedView, section: path });
    }
  }

  get isDrawing() {
    return this.placingResource?.type === 'room';
  }

  @cached
  get unplacedDesks() {
    return this.useableDesks.filter(
      (desk) =>
        !desk.isNew &&
        !this.mapFeatures.some((feature) => feature.externalId === desk.id && !this.deletableFeatures.includes(feature))
    );
  }

  get warnings() {
    return {
      numberOfUnplacedDesks: this.unplacedDesks.length,
    };
  }

  get resourceOptions() {
    return {
      room: this.roomOptions,
      deliveryArea: this.deliveryAreaOptions,
      visitorArea: this.visitorAreaOptions,
      desk: this.deskOptions,
      pointOfInterest: this.poiOptions,
    };
  }

  get poiOptions() {
    const cannotCreatePOI = this.abilities.cannot('manage-point-of-interest desks');

    return {
      disabled: cannotCreatePOI,
      tooltip: cannotCreatePOI ? 'You need permissions to manage POIs.' : 'POI',
    };
  }

  get visitorAreaOptions() {
    let tooltip = this.showMapsPOI ? null : 'Visitor area';

    if (!this.state.visitorsSubscription) {
      tooltip = 'Visitors subscription required to add visitor areas to your floor plan.';
    }
    return {
      disabled: !this.state.visitorsSubscription,
      tooltip,
    };
  }

  get deskOptions() {
    let tooltip = this.showMapsPOI ? null : 'Desks';

    const cannotCreateDesk = this.abilities.cannot('manage-desk desks');

    if (!this.featureConfig.isEnabled('desks')) {
      tooltip = 'Workplace subscription required to add desks to your floor plan.';
    } else if (cannotCreateDesk) {
      tooltip = 'You need permissions to manage desks.';
    }

    return {
      disabled: !this.featureConfig.isEnabled('desks') || cannotCreateDesk,
      tooltip,
    };
  }

  get deliveryAreaOptions() {
    let tooltip = this.showMapsPOI ? null : 'Delivery area';

    if (!this.state.deliveriesSubscription) {
      tooltip = 'Deliveries subscription required to add delivery areas to your floor plan.';
    } else if (isEmpty(this.availableDeliveryAreas)) {
      tooltip = 'You have placed all of the delivery areas in your account.';
    }

    return {
      disabled: isEmpty(this.availableDeliveryAreas) || !this.state.deliveriesSubscription,
      tooltip,
    };
  }

  get roomOptions() {
    let tooltip = this.showMapsPOI ? null : 'Rooms';

    const roomsEnabled = this.featureConfig.isEnabled('rooms');

    if (!roomsEnabled) {
      tooltip = 'Workplace subscription required to add rooms to your floor plan.';
    } else if (isEmpty(this.availableRooms)) {
      tooltip = 'You have placed all of the rooms in your account.';
    }

    return {
      disabled: !roomsEnabled || isEmpty(this.availableRooms),
      tooltip,
    };
  }

  get availableResources() {
    return {
      rooms: this.availableRooms,
      deliveryAreas: this.availableDeliveryAreas,
      unplacedDesks: this.unplacedDesks,
    };
  }

  _placedResourcesIds(type) {
    return this.mapFeatures.filter((feature) => feature.type === type && feature.externalId).map((f) => f.externalId);
  }

  get availableRooms() {
    const placedRoomIds = this._placedResourcesIds('room');
    return this.rooms.filter(({ id }) => !placedRoomIds.includes(id));
  }

  get availableDeliveryAreas() {
    const placedDeliveryAreasIds = this._placedResourcesIds('delivery-area');
    return this.deliveryAreas.filter(({ id }) => !placedDeliveryAreasIds.includes(id));
  }

  @action
  drawArea(resource) {
    if (this.isDrawing) return;
    this.placingResource = resource;
    this.drawingArea = new leaflet.Draw.Polygon(this.leafletMap, POLYGON_DRAWING_OPTIONS);
    this.drawingArea.enable();
    this.leafletMap.dragging.disable();
  }

  @action
  placeResource(resource) {
    if (this.isDrawing && this.drawingArea) {
      this.drawingArea.disable();
      this.leafletMap.dragging.enable();
    }
    if (resource?.type === 'desk') {
      this.deskResourceSelections += 1;
    }
    this.placingResource = resource;
  }

  @action
  setPlacingResource(resource) {
    if (resource?.type === 'desk') {
      this.deskResourceSelections += 1;
    }
    this.placingResource = resource;
  }

  get saveDisabled() {
    return (
      !(isPresent(this.dirtyDesks) || isPresent(this.dirtyFeatures) || isPresent(this.deletableFeatures)) ||
      isPresent(this.errorFeatures)
    );
  }

  @action
  registerEventListeners() {
    window.addEventListener('keydown', this.keyDownHandler);
  }

  @action
  keyDownHandler(event) {
    if ((event.key === 'Meta' || event.key === 'Control') && this.placingResource?.type === 'desk') {
      this.deskResourceSelections += 1;
    }
  }

  @action
  resetData() {
    this.employees = [];
    this.employeesPage = 0;
    this.employeesCount = 0;
    this.selectedFeature = null;
    this.placingResource = null;

    this.selectedPanelView = '';
    this.selectedNodeId = null;
    this.selectedEmployeeEmail = null;
    window.removeEventListener('keydown', this.keyDownHandler);
  }

  @action
  onMapFloorChange(mapFloor) {
    this.selectedFeature = null;
    void this.router.transitionTo('spaces.maps.edit.show', mapFloor.id);
  }

  @action
  setEmployeeFormVisibility(shouldShowEmployeeForm) {
    if (!shouldShowEmployeeForm) {
      this.selectedEmployeeEmail = null;
    }
    return (this.shouldShowEmployeeForm = shouldShowEmployeeForm);
  }

  @action
  onSelectedFeatureChange(feature) {
    this.selectedFeature = feature;
    this.selectedNodeId = feature?.id ?? feature?.tempId;
    this.deskResourceSelections += 1;
    if (feature) {
      let desk = null;
      if (feature.type === 'desk' && feature.externalId) {
        desk = this.store.peekRecord('desk', feature.externalId);
        if (desk && desk.assignedTo) {
          this.selectedEmployeeEmail = desk.assignedTo;
        } else {
          this.selectedEmployeeEmail = null;
        }
      } else {
        this.selectedEmployeeEmail = null;
      }

      const expandedPath = expandSectionOnFeatureSelect(
        feature,
        desk,
        this.selectedPanelView,
        this.flattenedAssignedEmployees
      );

      if (!this.expandedMap.includes(expandedPath)) {
        this.setExpandedMap(expandedPath);
      }
    }
  }

  @action
  onSelectedEmployeeChange(employee) {
    if (employee) {
      this.selectedEmployeeEmail = employee.email;
      this.setEmployeeFormVisibility(true);
    }
    this.selectedFeature = null;
  }

  get flattenedUnassignedEmployees() {
    return getFlattenedEmployees(this.gqlEmployees.unassigned);
  }

  get flattenedAssignedEmployees() {
    return getFlattenedEmployees(this.gqlEmployees.assigned);
  }

  get unassignedEmployeeInsightCount() {
    return this.flattenedUnassignedEmployees.filter((it) => {
      return it.attendancePerformance?.insights.meetsAttendancePolicy === true;
    }).length;
  }

  get assignedEmployeeInsightCount() {
    return this.flattenedAssignedEmployees.filter((it) => {
      return it.attendancePerformance?.insights.meetsAttendancePolicy == false;
    }).length;
  }

  get insightsCount() {
    return this.assignedEmployeeInsightCount + this.unassignedEmployeeInsightCount;
  }

  @action
  updateEmployeeOverview(employeeId, assignedDesks) {
    const employee = this.store.peekRecord('employee', employeeId);
    const gqlEmployees = [...this.flattenedAssignedEmployees, ...this.flattenedUnassignedEmployees];

    // since the attendance performance of the employee is not available in the ember store, we fetch it from
    // the GQL employee data to include in the subsequent employeeToChange variable
    const matchingGQLEmployee = gqlEmployees.find((it) => it.id === parseInt(employeeId));
    let employeeAttendancePerformance = null;
    if (matchingGQLEmployee) {
      employeeAttendancePerformance = matchingGQLEmployee.attendancePerformance;
      if (matchingGQLEmployee.department) {
        const employeeSection = assignedDesks.length > 0 ? 'assigned' : 'unassigned';
        if (!this.expandedMap.includes(`seating/${employeeSection}/${matchingGQLEmployee.department}`)) {
          this.setExpandedMap(`seating/${employeeSection}/${matchingGQLEmployee.department}`);
        }
      }
    }

    if (!employee) return;
    const newAssignedDesks = assignedDesks ?? [];

    // attendancePerformance has to come from the GQLEmployee and not the one in ember data
    const employeeToChange = buildCustomEmployeeItem(
      employee.id,
      employee.name,
      employee.email,
      employee.department,
      newAssignedDesks,
      employeeAttendancePerformance
    );

    // put all the existing employees into a single array, remove the incoming employee, and add back the changed version
    const employeesToReturn = getFlattenedEmployees(
      gqlEmployees.filter((it) => it.id !== employeeToChange.id).concat(employeeToChange)
    );

    // set the new assigned and unassigned sections and change the selected employee if needed
    const newAssignedEmployees = employeesToReturn.filter((employee) => employee.assignedDesks.length > 0);
    const newUnassignedEmployees = employeesToReturn.filter((employee) => employee.assignedDesks.length === 0);
    if (newAssignedEmployees.filter((it) => it.email === employee.email).length >= 1) {
      this.selectedEmployeeEmail = employee.email;
    }

    this.gqlEmployees = {
      unassigned: this.resourceOverview.applyDepartmentGroupings(newUnassignedEmployees),
      assigned: this.resourceOverview.applyDepartmentGroupings(newAssignedEmployees),
    };
  }

  @action
  updateResourceOverview(op, feature) {
    const modifiedFeature = {
      featureId: feature.id ?? feature.tempId,
      id: feature.externalId,
      name: feature.name,
      display: feature.name,
      isLastExpanded: true,
    };

    if (op === 'create') {
      addFeatureToOverview(this.gqlMapFeatures, feature, modifiedFeature);
    }

    if (op === 'update') {
      let desk;
      if (feature.externalId) {
        desk = this.store.peekRecord('desk', feature.externalId);
      } else {
        desk = feature.desk;
      }
      updateFeatureInOverview(this.gqlMapFeatures, feature, modifiedFeature, desk);
    }

    if (op === 'delete') {
      deleteFeatureInOverview(this.gqlMapFeatures, feature, modifiedFeature);
    }
  }

  _setNumberOfDesksInNeighborhoods() {
    const neighborhoodMap = {};

    this.desks.forEach((desk) => {
      const neighborhoodName = desk?.neighborhoodName;

      if (neighborhoodName) {
        if (neighborhoodMap[neighborhoodName]) {
          neighborhoodMap[neighborhoodName] += 1;
        } else {
          neighborhoodMap[neighborhoodName] = 1;
        }
      }
    });

    this.neighborhoods.forEach((neighborhood) => {
      if (neighborhoodMap[neighborhood.name]) {
        set(neighborhood, 'numberOfDesks', neighborhoodMap[neighborhood.name]);
      } else {
        set(neighborhood, 'numberOfDesks', 0);
      }
    });
  }

  fetchResourceOverviewDataTask = task({ drop: true }, async () => {
    const gqlMapFeaturesAndEmployees = await this.resourceOverview.getAllGQLDataTask.perform(
      this.model.areaMap,
      this.model.mapFloor
    );
    this.gqlMapFeatures = gqlMapFeaturesAndEmployees[0];
    const employeeData = gqlMapFeaturesAndEmployees[1];
    this.gqlEmployees = {
      unassigned: employeeData.unassigned,
      assigned: employeeData.assigned,
    };
  });

  loadResoucesDataTask = task({ drop: true }, async () => {
    const currentLocationId = this.currentLocation.id;
    [this.rooms, this.deliveryAreas, this.desks, this.neighborhoods, this.amenities, this.resourceInsights] = await all(
      [
        this.store.peekAll('room', { filter: { location: currentLocationId } }),
        this.store.query('delivery-area', { filter: { location: currentLocationId } }),
        this.store
          .query('desk', {
            filter: {
              'location-id': currentLocationId,
              'floor-id': get(this.model.mapFloor, 'id'),
              'include-draft-desks': true,
            },
            include: 'amenities',
          })
          .then((desks) => desks.toArray()),
        this.store
          .query('neighborhood', { filter: { 'location-id': currentLocationId } })
          .then((neighborhoods) => neighborhoods.toArray()),
        this.store.query('amenity', { filter: { 'location-id': currentLocationId } }),
        this.resourceOverview.getResourceInsightsTask.perform(this.model.areaMap.id, this.model.mapFloor.id),
        this.loadEmployeesTask.perform(),
      ]
    );
    await this.loadAssignedEmployeesTask.perform();
    this._setNumberOfDesksInNeighborhoods();
  });

  get employeeQuery() {
    const { limit, employeesPage } = this;
    const offset = employeesPage * limit;
    return {
      page: { limit, offset },
      filter: {
        locations: this.currentLocation.id,
        deleted: false,
      },
      include: 'assistants',
      sort: 'name',
    };
  }

  get hasMoreEmployeePages() {
    return this.employeesPage * this.limit < this.employeesCount;
  }

  loadEmployeesTask = task({ enqueue: true }, async () => {
    const employees = await this.store.query('employee', this.employeeQuery);
    this.employeesCount = employees.meta.total;
    this.employees.pushObjects(employees.toArray());
    this.employees = this.employees.filter((el) => el !== 'loadMore').uniqBy('email');
    this.employeesPage++;

    if (this.hasMoreEmployeePages) {
      this.employees.pushObject('loadMore');
    }
  });

  loadMoreEmployeesTask = task({ drop: true }, async () => {
    if (!this.hasMoreEmployeePages) return;
    try {
      await this.loadEmployeesTask.perform();
    } catch (e) {
      this.employeesPage--;
    }
  });

  loadAssignedEmployeesTask = task({ restartable: true }, async () => {
    const employeesPromises = [];
    let employeeResponses = [];
    const emailLimit = 50;
    const assignedToEmails = this.model.desksInLocation
      .filter((desk) => desk.assignedTo)
      .map((desk) => desk.assignedTo);

    const userEmails = assignedToEmails;
    let offset = 0;
    let page = 0;
    while (offset < userEmails.length) {
      const userEmailsPaginated = userEmails.slice(offset, offset + emailLimit);
      const fetchedEmployeesPromise = this.store.query('employee', {
        filter: {
          locations: this.state.currentLocation.id,
          'email-in': userEmailsPaginated.join(','),
          deleted: false,
        },
        include: 'user',
        page: { limit: 100, offset: 0 },
      });
      page++;
      offset = page * emailLimit;
      employeesPromises.push(fetchedEmployeesPromise);
    }
    employeeResponses = await all(employeesPromises);
    const employees = [];
    employeeResponses.forEach((response) => response.forEach((employee) => employees.push(employee)));
    this.employees.pushObjects(employees);
    this.employees = this.employees.filter((el) => el !== 'loadMore').uniqBy('email');
    if (this.hasMoreEmployeePages) {
      this.employees.pushObject('loadMore');
    }
  });

  get sortedMapFloors() {
    const mapFloors = [...this.model.areaMap.mapFloors.sortBy('ordinality')];
    if (this.abilities.can('manage-floor maps')) {
      mapFloors.push('create');
    }
    return mapFloors;
  }

  get hasMapFloorWithRasterImage() {
    return this.sortedMapFloors.some((floor) => floor.rasterImageUrl);
  }

  trackSaveEventPayload() {
    const saveEventObject = {
      deleted: { features: {} },
      created: { features: {} },
      updated: { features: {} },
    };

    const getFeatureObj = ({ name, geometry, enabled }) => {
      return {
        name,
        geometry,
        enabled,
      };
    };

    this.deletableFeatures.forEach((feature) => {
      if (saveEventObject.deleted.features[feature.type]) {
        saveEventObject.deleted.features[feature.type].push(feature.id);
      } else {
        saveEventObject.deleted.features[feature.type] = [feature.id];
      }
    });
    const deletedDeskIds = this.dirtyDesks.filter((desk) => desk.isDeleted).map((desk) => desk.id);
    saveEventObject.deleted['desks'] = deletedDeskIds;

    const newFeatures = this.dirtyFeatures.filterBy('isNew');
    newFeatures.forEach((feature) => {
      const featureObj = getFeatureObj(feature);
      if (saveEventObject.created.features[feature.type]) {
        saveEventObject.created.features[feature.type].push(featureObj);
      } else {
        saveEventObject.created.features[feature.type] = [featureObj];
      }
    });

    const newDesks = this.dirtyDesks.filterBy('isNew');
    saveEventObject.created['desks'] = newDesks.map(({ name, neighborhood, assignedTo, enabled, xPos, yPos }) => ({
      name,
      neighborhood,
      assignedTo,
      enabled,
      xPos,
      yPos,
    }));

    const getUpdatedAttributes = (record) => {
      const updatedObj = { id: record.id };
      const changed = record.changedAttributes();
      for (const attr in changed) {
        updatedObj[attr] = changed[attr][1];
      }
      return updatedObj;
    };

    const updatedFeatures = this.dirtyFeatures.filter((feature) => !feature.isNew && !feature.isDeleted);
    updatedFeatures.forEach((feature) => {
      const featureObj = getUpdatedAttributes(feature);
      if (saveEventObject.updated.features[feature.type]) {
        saveEventObject.updated.features[feature.type].push(featureObj);
      } else {
        saveEventObject.updated.features[feature.type] = [featureObj];
      }
    });

    const updatedDesks = this.dirtyDesks.filter((desk) => !desk.isNew && !desk.isDeleted);
    saveEventObject.updated['desks'] = updatedDesks.map((desk) => getUpdatedAttributes(desk));

    return saveEventObject;
  }

  saveTask = task({ drop: true }, async () => {
    const saveEventPayload = this.trackSaveEventPayload();
    try {
      const shouldSetDeskLocations = this.dirtyDesks.length || this.desks.filterBy('isDeleted').length;

      if (this.featureFlags.isEnabled('bulk-map-operations')) {
        // When creating or deleting desks the backend will create/delete the associated feature
        // This is why mapFeatures are fetched after the desks are created/deleted
        await all([this.createDesksTask.perform(), this.updateDesksTask.perform(), this.deleteDesksTask.perform()]);
        this.desks = this.store
          .peekAll('desk')
          .toArray()
          .filter((desk) => desk.belongsTo('floor').id() === this.model.mapFloor.id);
      } else {
        await all(this.deletableFeatures.filter((feature) => feature.type !== 'desk').invoke('destroyRecord'));
        await all(this.desks.filterBy('isDeleted').rejectBy('isDestroyed').invoke('save'));
        await all(this.dirtyDesks.invoke('save'));
      }

      if (this.featureFlags.isEnabled('bulk-map-operations')) {
        await all([
          this.createFeaturesTask.perform(),
          this.updateFeaturesTask.perform(),
          this.deleteFeaturesTask.perform(),
        ]);
      } else {
        // When desks are saved the features are updated in the backend so we filter out the desk feature to reduce the number of requests
        await all(this.dirtyFeatures.filter((feature) => feature.type !== 'desk').invoke('save'));
      }

      const promiseArray = [];
      promiseArray.push(
        this.store.query('map-feature', {
          filter: {
            'area-map': this.model.areaMap.id,
          },
        })
      );

      promiseArray.push(this.resourceOverview.getAllGQLDataTask.perform(this.model.areaMap, this.model.mapFloor));

      if (shouldSetDeskLocations) {
        promiseArray.push(this.state.setDeskLocationsTask.perform());
      }

      const [mapFeatures, gqlMapFeaturesAndEmployees, _setDeskLocations] = await all(promiseArray);

      this.mapFeatures = mapFeatures.toArray();
      this.deletableFeatures = [];
      this.shouldResetMap = true;

      this.selectedFeature = null;
      this.selectedNodeId = null;
      this.selectedEmployeeEmail = null;
      this.setEmployeeFormVisibility(false);

      this.gqlMapFeatures = gqlMapFeaturesAndEmployees[0];
      this.gqlEmployees = gqlMapFeaturesAndEmployees[1];

      this.flashMessages.showAndHideFlash('success', 'Saved!');
    } catch (e) {
      this.flashMessages.showFlash('error', parseErrorForDisplay(e));
    }

    this.metrics.trackEvent('[dashboard] Map - Saved', saveEventPayload);
  });

  get updateDesksPayload() {
    return this.dirtyDesks
      .filter((desk) => !desk.isNew && !desk.isDeleted)
      .map((desk) => {
        const neighborhoodId = desk?.belongsTo('neighborhood')?.id();

        const payload = {
          op: 'update',
          data: {
            type: 'resource',
            id: desk.id,
            attributes: {
              name: desk.name,
              'neighborhood-id': neighborhoodId,
              enabled: desk.enabled,
              'assigned-to': desk.assignedTo,
            },
            relationships: {
              amenities: {
                data: desk
                  .hasMany('amenities')
                  .ids()
                  .map((amenityId) => ({
                    type: 'amenities',
                    id: amenityId,
                  })),
              },
              floor: {
                data: {
                  type: 'floors',
                  id: desk.floorId,
                },
              },
              location: {
                data: {
                  type: 'locations',
                  id: this.currentLocation.id,
                },
              },
              neighborhood: {
                data: neighborhoodId
                  ? {
                      type: 'neighborhoods',
                      id: neighborhoodId,
                    }
                  : null,
              },
            },
          },
        };

        // Only include x-pos and y-pos if both are defined and not null
        if (desk.xPos != null && desk.yPos != null) {
          payload.data.attributes.geometry = {
            type: 'Point',
            coordinates: [desk.xPos, desk.yPos],
          };
        }

        return payload;
      });
  }

  updateDesksTask = task({ drop: true }, async () => {
    if (this.updateDesksPayload.length === 0) {
      return;
    }
    const url = urlBuilder.rms.bulkDesksUpdateEndpoint();
    await this.ajax
      .request(url, {
        accept: 'application/vnd.api+json',
        contentType: 'application/json',
        type: 'POST',
        data: JSON.stringify({ 'atomic:operations': this.updateDesksPayload }),
      })
      .then((response) => {
        response.data.forEach((desk) => {
          this.store.pushPayload('desk', createDeskStorePayload(desk));
        });
      });
  });

  get createDesksPayload() {
    return this.dirtyDesks
      .filter((desk) => desk.isNew)
      .map((desk) => {
        const neighborhoodId = desk?.belongsTo('neighborhood')?.id();

        const payload = {
          op: 'add',
          data: {
            type: 'resource',
            attributes: {
              name: desk.name,
              'resource-type-id': 1,
              'neighborhood-id': neighborhoodId,
              enabled: desk.enabled,
              'assigned-to': desk.assignedTo,
            },
            relationships: {
              amenities: {
                data: desk
                  .hasMany('amenities')
                  .ids()
                  .map((amenityId) => ({
                    type: 'amenities',
                    id: amenityId,
                  })),
              },
              floor: {
                data: {
                  type: 'floors',
                  id: desk.floorId,
                },
              },
              location: {
                data: {
                  type: 'locations',
                  id: this.currentLocation.id,
                },
              },
              neighborhood: {
                data: neighborhoodId
                  ? {
                      type: 'neighborhoods',
                      id: neighborhoodId,
                    }
                  : null,
              },
            },
          },
        };

        // Only include geometry if both xPos and yPos are defined and not null
        if (desk.xPos != null && desk.yPos != null) {
          payload.data.attributes.geometry = {
            type: 'Point',
            coordinates: [desk.xPos, desk.yPos],
          };
        }

        return payload;
      });
  }

  createDesksTask = task({ drop: true }, async () => {
    if (this.createDesksPayload.length === 0) {
      return;
    }
    const url = urlBuilder.rms.bulkDesksCreateEndpoint();

    // attempt to create multiple desks via the bulk-desk-creation endpoint
    await this.ajax
      .request(url, {
        accept: 'application/vnd.api+json',
        contentType: 'application/json',
        type: 'POST',
        data: JSON.stringify({ 'atomic:operations': this.createDesksPayload }),
      })
      .then((response) => {
        this.dirtyDesks
          .filter((desk) => desk.isNew)
          .forEach((dirtyDesk) => {
            dirtyDesk.unloadRecord();
          });
        response.data.forEach((desk) => {
          this.store.pushPayload('desk', createDeskStorePayload(desk));
        });

        return response;
      });
  });

  get deleteDesksPayload() {
    return this.desks
      .filter((desk) => desk.isDeleted && !desk.isNew)
      .map((desk) => {
        return {
          op: 'remove',
          data: {
            id: desk.id,
          },
        };
      });
  }

  deleteDesksTask = task({ drop: true }, async () => {
    if (this.deleteDesksPayload.length === 0) {
      return;
    }
    const url = urlBuilder.rms.bulkDesksDeleteEndpoint();

    await this.ajax
      .request(url, {
        accept: 'application/vnd.api+json',
        contentType: 'application/json',
        type: 'POST',
        data: JSON.stringify({ 'atomic:operations': this.deleteDesksPayload }),
      })
      .then(() => {
        this.desks
          .filter((desk) => desk.isDeleted)
          .forEach((deletedDesk) => {
            deletedDesk.unloadRecord();
          });
      });
  });

  get createNonDeskFeaturesPayload() {
    // Desks are filtered from the payload because the backend will manage feature creation when new desks are saved
    return this.dirtyFeatures
      .filter((feature) => feature.isNew && feature.type !== 'desk')
      .map((feature) => {
        // If the feature type is a polygon, we need to close the linestring
        const geometryType = feature.geometry.type;
        let coordinates = feature.geometry.coordinates;
        const { length, 0: start, [length - 1]: end } = coordinates;

        if (geometryType === 'Polygon' && !_isEqual(start, end)) {
          coordinates = [...coordinates, start];
        }
        return {
          op: 'add',
          data: {
            type: 'features',
            attributes: {
              geometry: {
                coordinates: coordinates,
                type: geometryType,
              },
              enabled: feature.enabled,
              type: feature.type.toUpperCase().replace(/-/g, '_'),
              externalId: feature.externalId,
              notes: feature.notes,
              name: feature.name,
            },
            relationships: {
              'map-floor': {
                data: {
                  type: 'map-floors',
                  id: feature.belongsTo('mapFloor').id(),
                },
              },
              'area-map': {
                data: {
                  type: 'area-maps',
                  id: feature.belongsTo('areaMap').id(),
                },
              },
            },
          },
        };
      });
  }

  createFeaturesTask = task({ drop: true }, async () => {
    if (this.createNonDeskFeaturesPayload.length === 0) {
      return;
    }
    const url = urlBuilder.maps.bulkFeaturesCreateEndpoint();
    await this.ajax.request(url, {
      accept: 'application/vnd.api+json',
      contentType: 'application/json',
      type: 'POST',
      data: JSON.stringify({ 'atomic:operations': this.createNonDeskFeaturesPayload }),
    });

    this.dirtyFeatures
      .filter((feature) => feature.isNew)
      .forEach((dirtyFeature) => {
        dirtyFeature.unloadRecord();
      });
  });

  get updateNonDeskFeaturesPayload() {
    return this.dirtyFeatures
      .filter((feature) => !feature.isNew && !feature.isDeleted && feature.type !== 'desk')
      .map((feature) => {
        // If the feature type is a polygon, we need to close the linestring
        const geometryType = feature.geometry.type;
        let coordinates = feature.geometry.coordinates;
        const { length, 0: start, [length - 1]: end } = coordinates;

        if (geometryType === 'Polygon' && !_isEqual(start, end)) {
          coordinates = [...coordinates, start];
        }

        return {
          op: 'update',
          data: {
            type: 'features',
            id: feature.id,
            attributes: {
              geometry: {
                coordinates: coordinates,
                type: geometryType,
              },
              enabled: feature.enabled,
              type: feature.type.toUpperCase().replace(/-/g, '_'),
              externalId: feature.externalId,
              notes: feature.notes,
              name: feature.name,
            },
          },
        };
      });
  }

  updateFeaturesTask = task({ drop: true }, async () => {
    if (this.updateNonDeskFeaturesPayload.length === 0) {
      return;
    }
    const url = urlBuilder.maps.bulkFeaturesUpdateEndpoint();
    await this.ajax.request(url, {
      accept: 'application/vnd.api+json',
      contentType: 'application/json',
      type: 'POST',
      data: JSON.stringify({ 'atomic:operations': this.updateNonDeskFeaturesPayload }),
    });
  });

  get deleteNonDeskFeaturesPayload() {
    // Desks are filtered from the payload because the backend will manage feature deletion when new desks are saved
    return this.deletableFeatures
      .filter((feature) => feature.type !== 'desk')
      .map((feature) => {
        return {
          op: 'remove',
          data: {
            type: 'features',
            id: feature.id,
          },
        };
      });
  }

  deleteFeaturesTask = task({ drop: true }, async () => {
    if (this.deleteNonDeskFeaturesPayload.length === 0) {
      return;
    }
    const url = urlBuilder.maps.bulkFeaturesDeleteEndpoint();
    await this.ajax.request(url, {
      accept: 'application/vnd.api+json',
      contentType: 'application/json',
      type: 'POST',
      data: JSON.stringify({ 'atomic:operations': this.deleteNonDeskFeaturesPayload }),
    });

    this.dirtyFeatures
      .filter((feature) => feature.isDeleted)
      .forEach((dirtyFeature) => {
        dirtyFeature.unloadRecord();
      });
  });
}
